import {
  Avatar,
  Box,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { CancelButton } from "../Controls/CancelButton";
import QueueStyle from "../Styled/Queue.style";
import useUpdateEffectOnce from "../../utils/hooks/useUpdateEffectOnce";
import api from "../../api/api";
import ArrayX from "../../utils/types/ArrayX";
import getDate from "../../utils/helpers/getDate";
import hasNoValue from "../../utils/helpers/hasNoValue";
import relativeTimeFormatter from "../../utils/helpers/relativeTimeFormatter";
import { Info } from "phosphor-react";

export default function DealAuditHistory({ open, onClose, dealInfo, idKey }) {
  const [dealAuditHistory, setDealAuditHistory] = useState({});

  useUpdateEffectOnce(() => {
    (async () => {
      const dealAudit = await api.getDealAudit(dealInfo?.[idKey]);
      const dealAuditArrayX = new ArrayX(...dealAudit).map(item => ({
        ...item,
        auditDate: getDate(item?.auditDate)[1],
      }));
      const groupedByDate = dealAuditArrayX.toGroup(({ auditDate }) => auditDate);
      setDealAuditHistory(groupedByDate);
    })();
  }, true);

  const dateGroups = Object.keys(dealAuditHistory);

  const normalizeVal = v => (v == null || v === "") ? null : v;

  const groupByUser = (updates) =>
    updates.reduce((acc, update) => {
      const userKey = update.userEmail;
      if (!acc[userKey]) acc[userKey] = [];
      acc[userKey].push(update);
      return acc;
    }, {});

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ fontWeight: 700, fontSize: "1.1rem", pb: 1 }}>
        Deal Update History
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {hasNoValue(dealAuditHistory) ? (
          <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
            No history to display
          </Typography>
        ) : (
          <Box sx={{ position: "relative", pl: "28px" }}>
            {/* Continuous vertical line */}
            <Box
              sx={{
                position: "absolute",
                left: "10px",
                top: "8px",
                bottom: "8px",
                width: "2px",
                bgcolor: "divider",
              }}
            />

            {dateGroups.map((key, groupIndex) => {
              const userGroups = groupByUser(dealAuditHistory[key]);
              const users = Object.keys(userGroups);

              return (
                <Box
                  key={key}
                  sx={{ position: "relative", mb: groupIndex < dateGroups.length - 1 ? 4 : 0 }}
                >
                  {/* Timeline dot */}
                  <Box
                    sx={{
                      position: "absolute",
                      left: "-24px",
                      top: "5px",
                      width: "12px",
                      height: "12px",
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      border: "2px solid",
                      borderColor: "background.paper",
                      boxShadow: 1,
                    }}
                  />

                  <Tooltip
                    title={new Intl.DateTimeFormat("en-ZA", {
                      dateStyle: "full",
                      timeStyle: "long",
                    }).format(new Date(key))}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: "0.07em",
                        cursor: "default",
                      }}
                    >
                      {relativeTimeFormatter(key)}
                    </Typography>
                  </Tooltip>

                  <Stack gap={1.5} mt={1}>
                    {users.map((userEmail) => {
                      const updates = userGroups[userEmail];
                      const operation = updates[0]?.operation;
                      const diffUpdates = updates.filter(u =>
                        u.operation !== "info" &&
                        normalizeVal(u.oldValue) !== normalizeVal(u.newValue)
                      );
                      const infoUpdates = updates.filter(u => u.operation === "info");
                      const nullStringUpdates = updates.filter(u =>
                        u.operation !== "info" &&
                        normalizeVal(u.oldValue) === normalizeVal(u.newValue)
                      );

                      if (diffUpdates.length === 0 && infoUpdates.length === 0 && nullStringUpdates.length === 0) return null;

                      return (
                        <Card
                          key={userEmail}
                          variant="outlined"
                          sx={{
                            p: 2,
                            borderRadius: 2,
                            borderColor: "divider",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                            transition: "box-shadow 0.2s",
                            "&:hover": { boxShadow: "0 3px 10px rgba(0,0,0,0.10)" },
                          }}
                        >
                          {/* Header row */}
                          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                            >
                              {userEmail}
                            </Typography>
                            <Chip
                              size="small"
                              color="info"
                              label={operation?.toUpperCase()}
                              sx={{ height: 18, fontSize: "0.55rem", ml: 1, flexShrink: 0 }}
                            />
                          </Stack>

                          {diffUpdates.length > 0 && (
                            <Box sx={{ bgcolor: "#F8F9FA", borderRadius: 1.5, p: 1.5 }}>
                              {diffUpdates.map((update, i) => (
                                <Box
                                  key={i}
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "120px 1fr",
                                    gap: 1,
                                    alignItems: "center",
                                    py: 0.6,
                                    borderBottom: "1px solid",
                                    borderColor: "divider",
                                    "&:last-child": { borderBottom: "none" },
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                  >
                                    {update.change}
                                  </Typography>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "wrap" }}>
                                    {(() => {
                                      const oldVal = normalizeVal(update.oldValue);
                                      const newVal = normalizeVal(update.newValue);
                                      return (
                                        <>
                                          <Tooltip title={oldVal ?? ""}>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "#94A3B8",
                                                textDecoration: "line-through",
                                                textDecorationColor: "#CBD5E1",
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                maxWidth: "110px",
                                              }}
                                            >
                                              {oldVal ?? <em style={{ opacity: 0.5 }}>empty</em>}
                                            </Typography>
                                          </Tooltip>
                                          <Typography variant="caption" sx={{ color: "#CBD5E1", lineHeight: 1 }}>→</Typography>
                                          <Tooltip title={newVal ?? ""}>
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                color: "#15803D",
                                                fontWeight: 600,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                                maxWidth: "110px",
                                              }}
                                            >
                                              {newVal ?? <em style={{ opacity: 0.5, fontWeight: 400 }}>empty</em>}
                                            </Typography>
                                          </Tooltip>
                                        </>
                                      );
                                    })()}
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}

                          {infoUpdates.length > 0 && (
                            <Box
                              sx={{
                                mt: diffUpdates.length > 0 ? 1 : 0,
                                bgcolor: "#F8F9FA",
                                borderRadius: "0 6px 6px 0",
                                px: 1.5,
                                py: 1,
                              }}
                            >
                              <Stack gap={0.5}>
                                {infoUpdates.map((update, i) => (
                                  <QueueStyle gap="6px" justifyContent="start">
                                    <Info color="blue" />
                                    <Typography key={i} variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                      {update.change}
                                    </Typography>
                                  </QueueStyle>
                                ))}
                              </Stack>
                            </Box>
                          )}

                          {nullStringUpdates.length > 0 && (
                            <Stack gap={0.5} mt={diffUpdates.length > 0 || infoUpdates.length > 0 ? 1 : 0}>
                              {nullStringUpdates.map((update, i) => (
                                <Typography
                                  key={i}
                                  variant="caption"
                                  sx={{ color: "text.disabled", fontStyle: "italic" }}
                                >
                                  {update.change} — null to empty string (no effective change)
                                </Typography>
                              ))}
                            </Stack>
                          )}
                        </Card>
                      );
                    })}
                  </Stack>
                </Box>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <QueueStyle>
          <CancelButton onClick={onClose} />
        </QueueStyle>
      </DialogActions>
    </Dialog>
  );
}
